<template>
    <div class="main-filters">
        <div class="main-filters__search">
            <el-input
                v-model="filters"
                style="width: 100%"
                :placeholder="translate('search_label')"
                @input='pesquisar'
            >
                <template #prefix>
                    <i class="el-icon-search"></i>
                </template>
            </el-input>
            <el-popover
                v-model="showModalAdvancedFilters"
                trigger="manual"
                width="570"
                offset="0"
            >
                <template #reference>
                    <el-button
                        slot="reference"
                        type="text"
                        icon="el-icon-s-operation"
                        class="main-filters__btn_advanced_filters"
                        @click="
                            showModalAdvancedFilters = !showModalAdvancedFilters
                        "
                    >
                        {{ translate('btn_advanced_filters') }}
                    </el-button>
                </template>
                <template #default>
                    <dialog-advanced-filters
                        v-if="showModalAdvancedFilters"
                        @handleFilter="handleFilter"
                        @clear="clearFilters"
                    />
                </template>
            </el-popover>
        </div>
        <div class="main-filters__selected">
            <h3>{{ translate('filters_selected') }}:</h3>
            <div class="main-filters__selected__options" v-if="hasFilters">
                <el-button v-if="filter.status.length" round size="small" @click="removeFilter({ status: [] })">
                    <div class="main-filters__selected__options_content">
                        <div
                            v-for="(status, index) in filter.status"
                            :key="status"
                            class="main-filters__selected__options_status"
                        >
                            <div>
                                {{
                                    $t(
                                        `contrato_rastreabilidade.messages.status.${statusColors[status].label}`
                                    )
                                }}
                            </div>
                            <div
                                v-if="
                                    filter.status.length > 1 &&
                                    index < filter.status.length - 1
                                "
                            >
                                ,
                            </div>
                        </div>
                        <div>
                            <i class="el-icon el-icon-close" />
                        </div>
                    </div>
                </el-button>
                <el-button
                    round
                    size="small"
                    v-if="filter.client"
                    @click="removeFilter({ client: '' })"
                >
                    <span>
                        {{ filter.client_name }}
                        <i class="el-icon el-icon-close" />
                    </span>
                </el-button>
                <el-button round size="small" v-if="filter.safra" @click="removeFilter({ safra: '' })">
                    <span>
                        {{ filter.safra }}
                        <i class="el-icon el-icon-close" />
                    </span>
                </el-button>
                <el-button round size="small" v-if="filter.cultivations.length" @click="removeFilter({ cultivations: [] })">
                    <span>
                        {{ filter.cultivations.join(', ') }}
                        <i class="el-icon el-icon-close" />
                    </span>
                </el-button>
                <el-button
                    v-if="hasFilters"
                    round
                    size="small"
                    class="main-filters__selected__options_status_clear"
                    @click="clearFilters"
                >
                    <span>
                        {{ translate('clear_filters') }}
                        <i class="el-icon el-icon-delete" />
                    </span>
                </el-button>
            </div>
            <span v-else>
                {{ translate('no_filters_selected') }}
            </span>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { statusColors } from '../../../../../../utils';

export default {
    name: 'index',
    components: {
        dialogAdvancedFilters: () =>
            import(
                '@/modules/traceability-contract/views/pages/main/components/shared/filters/dialog-advanced-filters.vue'
            )
    },
    data() {
        return {
            protocols: [],
            showModalAdvancedFilters: false,
            statusColors: statusColors,
            filters: ''
        };
    },
    computed: {
        ...mapState('traceabilityContractModule', ['filter']),
        hasFilters() {
            return (
                this.filter.contract_number ||
                this.filter.status?.length > 0 ||
                this.filter.client ||
                this.filter.safra ||
                this.filter.cultivations?.length > 0
            );
        }
    },
    methods: {
        ...mapActions('traceabilityContractModule', [
            'fetchTraceabilityReports'
        ]),
        translate(key, params = {}) {
            const base = 'contrato_rastreabilidade.messages.main_filters';
            return this.$t(`${base}.${key}`, params);
        },
        handleFilter() {
            this.showModalAdvancedFilters = false;
            this.filter.page = 0;
            const isRegistrant = this.$route.name === 'contrato-rastreabilidade';
            this.fetchTraceabilityReports(isRegistrant);
        },
        clearFilters() {
            this.showModalAdvancedFilters = false;
            this.$store.commit('traceabilityContractModule/CLEAR_FILTER');
        },
        removeFilter(key) {
            this.$store.commit('traceabilityContractModule/SET_FILTER', {
                ...this.filter,
                ...key
            });
        },
        pesquisar() {
            const searchQuery = this.filters.trim();

            if (searchQuery.length < 3 && searchQuery.length !== 0) {
                return;
            }

            if (isNaN(searchQuery)) {
                this.filter.contract_number = searchQuery.toLowerCase();
            } else {
                this.filter.contract_number = searchQuery;
            }

            this.$store.commit('traceabilityContractModule/SET_FILTER', {
                ...this.filter
            });
            this.fetchTraceabilityReports();
        }
    },
    beforeDestroy() {
        this.clearFilters();
    }
};
</script>

<style scoped lang="scss">
.main-filters {
    .main-filters__search {
        display: grid;
        grid-template-columns: 1fr auto;
        gap: 16px;
        @media (max-width: 768px) {
            grid-template-columns: 1fr;
        }

        .el-input {
            i.el-icon-search {
                color: #183b57;
                font-size: 20px;
                margin: 10px 2px;
            }
        }

        .main-filters__btn_advanced_filters {
            border: 1px solid #183b57;
            font-size: 14px;
            padding: 9px 16px;
            height: 40px;
        }
    }

    .main-filters__selected {
        > h3 {
            margin: 16px 0;
            color: #3b3b3b;
            font-weight: 500;
            font-size: 16px;
            padding-right: 10px;
        }

        > span {
            font-style: italic;
        }

        display: flex;
        align-items: center;

        .main-filters__selected__options {
            display: flex;
            align-items: center;

            .el-icon {
                box-sizing: border-box;
            }

            .el-icon:hover {
                background-color: rgba(255, 255, 255, 0.4);
                border-radius: 100%;
            }

            .main-filters__selected__options_content {
                display: flex;

                .main-filters__selected__options_status {
                    display: inline-flex;
                    padding-right: 5px;
                    text-transform: capitalize;
                }
            }

            .el-button {
                background-color: #359138;
                color: white;
                font-size: 12px;
                text-transform: lowercase;
                display: flex;
            }

            .main-filters__selected__options_status_clear {
                background-color: #ebf4eb;
                color: #359138;
                font-weight: 300;
                font-size: 12px;
                align-items: center;
            }
        }
    }
}
</style>
